
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

// Get all videos (for admin purposes)
export const getAllVideos = async () => {
  try {
    const { data, error } = await supabase
      .from('recordings')
      .select('*, likes(*)')
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Error fetching all videos:", error);
      return null;
    }

    return data || [];
  } catch (error) {
    console.error("Exception in getAllVideos:", error);
    return null;
  }
};

// Get merchant videos (videos recorded for merchants)
export const getMerchantVideos = async () => {
  try {
    // First approach: Join merchant_videos with recordings
    const { data, error } = await supabase
      .from('recordings')
      .select('*, likes(*)')
      .not('merchant_id', 'is', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Error fetching merchant videos:", error);
      return null;
    }

    // Process video data to ensure proper URL formats
    const processedVideos = data?.map(video => {
      // Handle video_path if it's stored as JSON string
      let videoPath = video.video_path;
      if (typeof videoPath === 'string') {
        try {
          // Check if it's a JSON string
          if (videoPath.startsWith('{') || videoPath.startsWith('[') || videoPath.startsWith('"')) {
            const parsed = JSON.parse(videoPath);
            videoPath = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
          }
        } catch (e) {
          // Not a JSON string, leave as is
        }
      }
      
      return {
        ...video,
        video_path: videoPath
      };
    }) || [];

    return processedVideos;
  } catch (error) {
    console.error("Exception in getMerchantVideos:", error);
    return null;
  }
};

// Get user videos (personal videos not for merchants)
export const getUserVideos = async () => {
  try {
    const { data, error } = await supabase
      .from('recordings')
      .select('*, likes(*)')
      .is('merchant_id', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Error fetching user videos:", error);
      return null;
    }

    // Process video data to ensure proper URL formats
    const processedVideos = data?.map(video => {
      // Handle video_path if it's stored as JSON string
      let videoPath = video.video_path;
      if (typeof videoPath === 'string') {
        try {
          // Check if it's a JSON string
          if (videoPath.startsWith('{') || videoPath.startsWith('[') || videoPath.startsWith('"')) {
            const parsed = JSON.parse(videoPath);
            videoPath = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
          }
        } catch (e) {
          // Not a JSON string, leave as is
        }
      }
      
      return {
        ...video,
        video_path: videoPath
      };
    }) || [];

    return processedVideos;
  } catch (error) {
    console.error("Exception in getUserVideos:", error);
    return null;
  }
};

// Like a video
export const likeVideo = async (videoId: string | number) => {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session || !session.session) {
      toast.error("You must be logged in to like videos");
      return { error: "Not authenticated" };
    }

    const userId = session.session.user.id;
    // Convert videoId to number for database consistency
    const numericVideoId = typeof videoId === 'string' ? parseInt(videoId, 10) : videoId;

    if (isNaN(numericVideoId)) {
      console.error("Invalid video ID:", videoId);
      return { error: "Invalid video ID" };
    }

    const { error } = await supabase
      .from('likes')
      .insert({
        user_id: userId,
        video_id: numericVideoId,
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error("Error liking video:", error);
      return { error };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in likeVideo:", error);
    return { error };
  }
};

// Unlike a video
export const unlikeVideo = async (videoId: string | number) => {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session || !session.session) {
      toast.error("You must be logged in to unlike videos");
      return { error: "Not authenticated" };
    }

    const userId = session.session.user.id;
    // Convert videoId to number for database consistency
    const numericVideoId = typeof videoId === 'string' ? parseInt(videoId, 10) : videoId;

    if (isNaN(numericVideoId)) {
      console.error("Invalid video ID:", videoId);
      return { error: "Invalid video ID" };
    }

    const { error } = await supabase
      .from('likes')
      .delete()
      .eq('user_id', userId)
      .eq('video_id', numericVideoId);

    if (error) {
      console.error("Error unliking video:", error);
      return { error };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in unlikeVideo:", error);
    return { error };
  }
};

// Get video likes count
export const getVideoLikesCount = async (videoId: string | number) => {
  try {
    // Convert videoId to number for database consistency
    const numericVideoId = typeof videoId === 'string' ? parseInt(videoId, 10) : videoId;

    if (isNaN(numericVideoId)) {
      console.error("Invalid video ID:", videoId);
      return { error: "Invalid video ID" };
    }

    const { count, error } = await supabase
      .from('likes')
      .select('*', { count: 'exact' })
      .eq('video_id', numericVideoId);

    if (error) {
      console.error("Error fetching likes count:", error);
      return { error };
    }

    return { count };
  } catch (error) {
    console.error("Exception in getVideoLikesCount:", error);
    return { error };
  }
};

// Delete a video
export const deleteVideo = async (videoId: string | number) => {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session || !session.session) {
      toast.error("You must be logged in to delete videos");
      return { error: "Not authenticated" };
    }

    // Convert videoId to number for database consistency
    const numericVideoId = typeof videoId === 'string' ? parseInt(videoId, 10) : videoId;

    if (isNaN(numericVideoId)) {
      console.error("Invalid video ID:", videoId);
      return { error: "Invalid video ID" };
    }

    const { error } = await supabase
      .from('recordings')
      .delete()
      .eq('id', numericVideoId)
      .eq('user_id', session.session.user.id);

    if (error) {
      console.error("Error deleting video:", error);
      return { error };
    }

    return { success: true };
  } catch (error) {
    console.error("Exception in deleteVideo:", error);
    return { error };
  }
};
